import { createRng } from './rng';

/**
 * The hazard: asteroids streaking in from the right and flying leftwards
 * across the arena, coming faster and more often the longer a racer survives.
 *
 * This is Matt's original Koodoo Racers design — rocks cross horizontally and
 * the racer dodges up and down, rather than rocks falling from above.
 *
 * Every rock is derived from a seed, so a field is fully reproducible. Two
 * players racing the same seed face exactly the same rocks in the same order,
 * which keeps a two-player race fair and is what a networked match will rely on.
 */
export interface Asteroid {
  id: number;
  /** Centre, in arena coordinates. */
  x: number;
  y: number;
  radius: number;
  /** Leftward speed in pixels per second. */
  speed: number;
  /** Radians per second, for rendering only — it does not affect collision. */
  spin: number;
  rotation: number;
}

export interface AsteroidField {
  seed: number;
  rngState: number;
  asteroids: Asteroid[];
  /** Milliseconds until the next spawn. */
  nextSpawnInMs: number;
  nextId: number;
  elapsedMs: number;
}

/**
 * The shared arena. Both racers fly in it at once — there is no split screen
 * and no per-player lane, so a body-check is as dangerous as a rock.
 */
export const ARENA_WIDTH = 1280;
export const ARENA_HEIGHT = 720;

export const MIN_RADIUS = 16;
export const MAX_RADIUS = 48;

const BASE_SPAWN_INTERVAL_MS = 620;
/**
 * The tightest rocks ever come. Exported because it is also the ceiling on how
 * many near misses a run can possibly contain, which is what lets the
 * leaderboard reject an impossible score — see `leaderboard.ts`.
 */
export const MIN_SPAWN_INTERVAL_MS = 190;
/** How quickly spawning tightens. Reaches the floor at roughly 60s. */
const SPAWN_RAMP_MS = 60_000;

/** Rocks move quickly — they are meant to streak past, not drift. */
const BASE_SPEED = 430;
const MAX_SPEED = 1_050;
const SPEED_RAMP_MS = 75_000;

/** Rocks are culled once fully past the left edge of the arena. */
const CULL_MARGIN = 120;

export function createAsteroidField(seed: number): AsteroidField {
  return {
    seed,
    rngState: createRng(seed).state,
    asteroids: [],
    // Give the racer a beat to breathe before the first rock arrives.
    nextSpawnInMs: 600,
    nextId: 1,
    elapsedMs: 0,
  };
}

/** Spawn interval at a given survival time, easing from base down to the floor. */
export function spawnIntervalAt(elapsedMs: number): number {
  const t = Math.min(elapsedMs / SPAWN_RAMP_MS, 1);
  return BASE_SPAWN_INTERVAL_MS - (BASE_SPAWN_INTERVAL_MS - MIN_SPAWN_INTERVAL_MS) * t;
}

/** Travel speed at a given survival time. */
export function flySpeedAt(elapsedMs: number): number {
  const t = Math.min(elapsedMs / SPEED_RAMP_MS, 1);
  return BASE_SPEED + (MAX_SPEED - BASE_SPEED) * t;
}

/**
 * Advances the field by `deltaMs`: moves every rock leftwards, spawns any that
 * are due, and culls the ones that have left the arena.
 *
 * Pure — returns a new field. Large deltas are handled by spawning repeatedly
 * within the step rather than dropping the extras.
 */
export function advanceField(field: AsteroidField, deltaMs: number): AsteroidField {
  if (deltaMs <= 0) {
    return field;
  }

  const rng = createRng(field.rngState);
  const seconds = deltaMs / 1000;

  const asteroids: Asteroid[] = field.asteroids.map((asteroid) => ({
    ...asteroid,
    x: asteroid.x - asteroid.speed * seconds,
    rotation: asteroid.rotation + asteroid.spin * seconds,
  }));

  let elapsedMs = field.elapsedMs;
  let nextSpawnInMs = field.nextSpawnInMs;
  let nextId = field.nextId;
  let remaining = deltaMs;

  while (remaining >= nextSpawnInMs) {
    remaining -= nextSpawnInMs;
    elapsedMs += nextSpawnInMs;

    const radius = rng.range(MIN_RADIUS, MAX_RADIUS);
    const speed = flySpeedAt(elapsedMs) * rng.range(0.85, 1.15);
    asteroids.push({
      id: nextId,
      // Enter from just off the right edge, backdated by however much of this
      // step is already spent so a mid-step spawn is not stuck at the border.
      x: ARENA_WIDTH + radius - speed * (remaining / 1000),
      y: rng.range(radius, ARENA_HEIGHT - radius),
      radius,
      speed,
      spin: rng.range(-2.2, 2.2),
      rotation: rng.range(0, Math.PI * 2),
    });

    nextId += 1;
    nextSpawnInMs = spawnIntervalAt(elapsedMs);
  }

  nextSpawnInMs -= remaining;
  elapsedMs += remaining;

  // Cull after spawning, not before: a single large delta can spawn a rock and
  // carry it clean past the left edge in the same step. Culling first would
  // keep it, so a backgrounded tab would end up with a field a steadily-
  // stepped simulation had already discarded — and the two would drift apart.
  const alive = asteroids.filter((asteroid) => asteroid.x + asteroid.radius >= -CULL_MARGIN);

  return {
    ...field,
    rngState: rng.state,
    asteroids: alive,
    nextSpawnInMs,
    nextId,
    elapsedMs,
  };
}

/** Replays a field from its seed. Same seed and steps produce the same rocks. */
export function isSameField(a: AsteroidField, b: AsteroidField): boolean {
  if (a.asteroids.length !== b.asteroids.length) {
    return false;
  }
  return a.asteroids.every((asteroid, index) => {
    const other = b.asteroids[index]!;
    return (
      asteroid.id === other.id &&
      asteroid.x === other.x &&
      asteroid.y === other.y &&
      asteroid.radius === other.radius
    );
  });
}
